import { ChatIcon, InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { askPat, AskPatResponse } from '../../services/askPat/askPatService';

export interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AskPatChatProps {
  isOpen?: boolean;
  onClose?: () => void;
  docType: 'market' | 'practice';
  docId: string;
  docName?: string;
  variant?: 'modal' | 'embedded';
}

// Generate a unique conversation ID
const generateConversationId = (): string => {
  return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getResourceHref = (
  resourceType: 'practice' | 'provider' | 'market',
  resourceId: string
): string => {
  switch (resourceType) {
    case 'practice':
      return `/practices/${resourceId}`;
    case 'provider':
      return `/providers/${resourceId}`;
    case 'market':
      return `/markets/${resourceId}`;
    default:
      return '';
  }
};

const renderMessageContent = (content: string): React.ReactNode[] => {
  const pattern = /\[([^\]|]+)\|(practice|provider|market)\|([^\]]+)\](\s*\^\s*)?/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(content)) !== null) {
    const [fullMatch, name, type, id, trailing] = match;
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      nodes.push(content.slice(lastIndex, matchIndex));
    }

    const href = getResourceHref(
      type as 'practice' | 'provider' | 'market',
      id
    );

    if (href) {
      nodes.push(
        <Link
          key={`${type}-${id}-${matchIndex}`}
          href={href}
          isExternal
          color="blue.600"
          textDecoration="underline"
          _hover={{ color: 'blue.700' }}
        >
          {name}
        </Link>
      );
    } else {
      nodes.push(fullMatch);
    }

    if (trailing) {
      nodes.push(trailing.replace(/\^/g, ''));
    }

    lastIndex = matchIndex + fullMatch.length;
  }

  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }

  return nodes;
};

const AskPatChat: React.FC<AskPatChatProps> = ({
  isOpen = true,
  onClose,
  docType,
  docId,
  docName,
  variant = 'modal',
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const isEmbedded = variant === 'embedded';

  // Initialize conversation ID when component mounts (for embedded) or modal opens
  useEffect(() => {
    if (isEmbedded || isOpen) {
      console.log('[AskPAT Chat] Component initialized', {
        variant,
        docType,
        docId,
        docName,
      });

      const newConversationId = generateConversationId();
      setConversationId(newConversationId);
      setMessages([]);
      setInputValue('');

      // Add welcome message
      setMessages([
        {
          id: 'welcome',
          type: 'assistant',
          content: `Hello! I'm PAT, your AI assistant. I can help answer questions about ${docName || `this ${docType}`}. What would you like to know?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isEmbedded, isOpen, docType, docId, docName, variant]);

  // Auto-scroll to bottom when new messages arrive (only within container, not window)
  useEffect(() => {
    if (messagesEndRef.current) {
      // Use block: 'nearest' to prevent window scroll, only scroll within container
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [messages]);

  // Focus input when modal opens (only for modal variant)
  useEffect(() => {
    if (!isEmbedded && isOpen) {
      setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 100);
    }
  }, [isOpen, isEmbedded]);

  const handleSendMessage = useCallback(async () => {
    const question = inputValue.trim();

    console.log('[AskPAT Chat] handleSendMessage called', {
      question,
      isLoading,
      conversationId,
      docType,
      docId,
    });

    if (!question || isLoading) {
      console.log('[AskPAT Chat] Early return - no question or loading');
      return;
    }

    // Add user message
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      console.log('[AskPAT Chat] Sending message', {
        question,
        docType,
        docId,
        conversation_id: conversationId,
      });

      const response: AskPatResponse = await askPat({
        question,
        docType,
        docId,
        conversation_id: conversationId,
      });

      console.log('[AskPAT Chat] Response received', response);

      // Extract answer from response (structure may change after testing)
      // Try multiple possible response formats
      let answer = '';

      if (typeof response === 'string') {
        answer = response;
      } else if (response.answer) {
        answer = response.answer;
      } else if (response.response) {
        answer = typeof response.response === 'string'
          ? response.response
          : JSON.stringify(response.response);
      } else if (response.message) {
        answer = typeof response.message === 'string'
          ? response.message
          : String(response.message);
      } else {
        // Fallback: stringify the whole response
        answer = JSON.stringify(response);
      }

      const assistantMessage: Message = {
        id: `assistant_${Date.now()}`,
        type: 'assistant',
        content: answer,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('[AskPAT Chat] Error in handleSendMessage', error);

      // Extract error message
      const errorContent = error instanceof Error
        ? error.message
        : 'Sorry, I encountered an error. Please try again.';

      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        type: 'assistant',
        content: errorContent,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);

      toast({
        title: 'Error',
        description: errorContent,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, docType, docId, conversationId, toast]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Chat content component (shared between modal and embedded)
  const chatContent = (
    <Box
      display="flex"
      flexDirection="column"
      height={isEmbedded ? '100%' : 'auto'}
      width={isEmbedded ? '100%' : 'auto'}
      maxWidth={isEmbedded ? '100%' : 'none'}
      bg="white"
      borderRadius={isEmbedded ? 'md' : 'xl'}
      border={isEmbedded ? '1px solid' : 'none'}
      borderColor={isEmbedded ? 'gray.200' : 'transparent'}
      boxShadow={isEmbedded ? 'sm' : '2xl'}
      overflow="hidden"
      flexShrink={0}
    >
      {/* Header */}
      <Box
        borderBottom="1px solid"
        borderColor="gray.200"
        pb={3}
        pt={3}
        px={4}
        bg="blue.50"
        borderTopRadius={isEmbedded ? 'md' : 'xl'}
        overflow="visible"
      >
        <Flex align="center" justify="space-between" gap={2}>
          <Flex align="center" gap={2} flex="1" minW="0">
            <ChatIcon color="blue.600" boxSize={4} flexShrink={0} />
            <Text fontSize="1rem" fontWeight="600" color="blue.700" noOfLines={1}>
              Ask PAT
            </Text>
            {docName && (
              <Text fontSize="0.75rem" color="gray.600" fontWeight="400" noOfLines={1}>
                - {docName}
              </Text>
            )}
          </Flex>
          <Popover
            trigger="hover"
            placement="bottom-end"
            closeOnBlur={false}
            isLazy
          >
            <PopoverTrigger>
              <IconButton
                aria-label="AI Information"
                icon={<InfoIcon />}
                size="sm"
                variant="ghost"
                color="blue.600"
                _hover={{ bg: 'blue.100', color: 'blue.700' }}
                borderRadius="full"
                minW="1.75rem"
                minH="1.75rem"
                w="1.75rem"
                h="1.75rem"
                flexShrink={0}
              />
            </PopoverTrigger>
            <PopoverContent
              maxW="280px"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="lg"
              zIndex={1000}
            >
              <PopoverBody p={3}>
                <VStack align="start" spacing={2}>
                  <Text fontSize="0.75rem" fontWeight="600" color="gray.700">
                    Powered by Gemini AI
                  </Text>
                  <Text fontSize="0.75rem" color="gray.600" lineHeight="1.4">
                    This assistant uses Google's Gemini AI to help answer questions about {docType === 'practice' ? 'practices' : 'markets'}. AI responses may contain errors or inaccuracies. Please verify important information independently.
                  </Text>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Flex>
      </Box>

      {/* Messages Container */}
      <Box
        flex="1"
        overflowY="auto"
        p={4}
        bg="gray.50"
        minH={isEmbedded ? '0' : '300px'}
        css={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a0aec0',
          },
        }}
      >
        <VStack spacing={3} align="stretch">
          {messages.map((message) => (
            <Flex
              key={message.id}
              justify={
                message.type === 'user' ? 'flex-end' : 'flex-start'
              }
            >
              <Box
                maxW="85%"
                p={2.5}
                borderRadius="lg"
                bg={message.type === 'user' ? 'blue.500' : 'white'}
                color={message.type === 'user' ? 'white' : 'gray.800'}
                boxShadow="sm"
                border={
                  message.type === 'assistant'
                    ? '1px solid'
                    : 'none'
                }
                borderColor={
                  message.type === 'assistant' ? 'gray.200' : 'transparent'
                }
              >
                <Text fontSize="0.875rem" whiteSpace="pre-wrap" lineHeight="1.4">
                  {renderMessageContent(message.content)}
                </Text>
                <Text
                  fontSize="0.625rem"
                  color={
                    message.type === 'user'
                      ? 'blue.100'
                      : 'gray.500'
                  }
                  mt={1}
                >
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              </Box>
            </Flex>
          ))}
          {isLoading && (
            <Flex justify="flex-start">
              <Box
                p={3}
                borderRadius="lg"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <Spinner size="sm" color="blue.500" />
              </Box>
            </Flex>
          )}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Input Container */}
      <Box
        p={3}
        borderTop="1px solid"
        borderColor="gray.200"
        bg="white"
        borderBottomRadius={isEmbedded ? 'md' : 'xl'}
      >
        <Flex gap={2}>
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask PAT a question..."
            isDisabled={isLoading}
            size="sm"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.300"
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px #3182ce',
            }}
          />
          <Button
            onClick={handleSendMessage}
            isDisabled={!inputValue.trim() || isLoading}
            isLoading={isLoading}
            loadingText="..."
            colorScheme="blue"
            size="sm"
            px={4}
          >
            Send
          </Button>
        </Flex>
      </Box>
    </Box>
  );

  // Return modal wrapper for modal variant, or direct content for embedded
  if (isEmbedded) {
    return chatContent;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose || (() => {})}
      size="xl"
      isCentered
      closeOnOverlayClick={true}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        maxW="600px"
        maxH="80vh"
        borderRadius="xl"
        boxShadow="2xl"
        display="flex"
        flexDirection="column"
        p={0}
      >
        <ModalCloseButton />
        <ModalBody p={0} overflow="hidden">
          {chatContent}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AskPatChat;

